<script setup>
import { computed } from 'vue'

const props = defineProps({
  visible: Boolean,
  salida: Object,
  conductores: { type: Array, default: () => [] },
  grupos: { type: Array, default: () => [] },
  zonas: { type: Array, default: () => [] },
  territoryMeetingPoints: { type: Array, default: () => [] },
  selectedPointIndex: { type: [Number, String], default: null },
  territoryKeys: { type: Array, default: () => [] },
  mode: { type: String, default: 'completo' },
  pendientes: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:visible', 'select-point', 'informar'])

const esPendientes = computed(() => props.mode === 'pendientes')

const close = () => emit('update:visible', false)

const selectedPoint = computed(() =>
  props.territoryMeetingPoints.find((p) => String(p.index) === String(props.selectedPointIndex)) || null
)

const onSelectPoint = (index) => {
  const point = props.territoryMeetingPoints.find((p) => String(p.index) === String(index))
  if (point) emit('select-point', point)
}

const onInformar = (key) => {
  emit('informar', key)
  close()
}

const directionsUrl = (p) => {
  const ll = p?.latlng
  const lat = ll?.lat ?? ll?.[0]
  const lng = ll?.lng ?? ll?.[1]
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
}

const getConductorName = (id) => {
  const found = props.conductores?.find((c) => c.id === id)
  return found ? found.name : (id || '')
}

const getGruposStr = (grupoIds) => {
  if (!grupoIds?.length) return ''
  return grupoIds
    .map((gId) => {
      const found = props.grupos?.find((g) => g.id === gId)
      const name = found ? found.name : gId
      const zona = found?.zonaId
        ? props.zonas?.find((z) => z.id === found.zonaId)?.name
        : null
      return zona ? `${name} — ${zona}` : name
    })
    .join(', ')
}

const getLocalDate = (d) => {
  if (!d) return ''
  return new Date(d).toLocaleDateString('es-AR')
}

const getDayName = (d) => {
  if (!d) return ''
  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
  return days[new Date(d).getDay()]
}

const waLink = (w) => 'https://wa.me/' + String(w).replace(/\D/g, '')
</script>

<template>
  <Transition name="sheet" :duration="{ enter: 350, leave: 300 }">
    <div v-if="visible && salida" class="sheet-wrapper">
      <div class="sheet-backdrop" @click="close" />
      <div class="info-sheet">
        <div class="drag-handle" />

        <div class="sheet-header">
          <span class="sheet-title">{{ esPendientes ? 'Qué falta trabajar' : 'Información de la salida' }}</span>
          <button class="sheet-close-btn" @click="close">
            <i class="pi pi-times" />
          </button>
        </div>

        <div v-if="esPendientes" class="pendientes-block">
          <div v-if="!pendientes.length" class="pendientes-vacio">
            No hay territorios asignados a esta salida.
          </div>
          <div v-for="p in pendientes" :key="p.key" class="pendiente-card">
            <div class="pendiente-head">
              <span class="pendiente-terr">Territorio {{ p.key }}</span>
              <span v-if="p.trabajado" class="pendiente-estado ok">
                <i class="pi pi-check" /> Completo
              </span>
              <span v-else class="pendiente-estado falta">Falta</span>
            </div>
            <div v-if="!p.trabajado && p.total" class="pendiente-mz">
              Manzanas pendientes:
              <strong>{{ p.pendientes.join(', ') }}</strong>
              <span class="pendiente-count">({{ p.pendientes.length }} de {{ p.total }})</span>
            </div>
            <div v-else-if="!p.trabajado" class="pendiente-mz">Territorio sin trabajar</div>
          </div>
        </div>

        <div v-if="!esPendientes" class="info-top-row">
          <div class="date-block">
            <span class="date-day">{{ getDayName(salida.date) }}</span>
            <span class="date-full">{{ getLocalDate(salida.date) }}</span>
            <span v-if="salida.hour" class="date-hour">{{ salida.hour }} hs</span>
          </div>
          <div class="meta-block">
            <span v-if="salida.conductor" class="meta-item">
              <i class="pi pi-user meta-icon" />
              {{ getConductorName(salida.conductor) }}
            </span>
            <span v-if="getGruposStr(salida.grupos)" class="meta-item">
              <i class="pi pi-users meta-icon" />
              Grupo {{ getGruposStr(salida.grupos) }}
            </span>
          </div>
        </div>

        <div
          v-if="!esPendientes && (salida.puntoEncuentro?.name || salida.puntoEncuentroTerritorio?.name)"
          class="chips-row"
        >
          <a
            v-if="salida.puntoEncuentro?.name"
            :href="`https://www.google.com/maps?q=${salida.puntoEncuentro.latlng?.lat},${salida.puntoEncuentro.latlng?.lng}`"
            target="_blank"
            class="location-chip chip-blue"
          >
            <i class="pi pi-map-marker chip-icon" />
            <span>{{ salida.puntoEncuentro.name }}</span>
            <i class="pi pi-external-link chip-ext" />
          </a>
          <a
            v-if="salida.puntoEncuentroTerritorio?.name"
            :href="`https://www.google.com/maps?q=${salida.puntoEncuentroTerritorio.latlng?.lat},${salida.puntoEncuentroTerritorio.latlng?.lng}`"
            target="_blank"
            class="location-chip chip-green"
          >
            <i class="pi pi-flag chip-icon" />
            <span>{{ salida.puntoEncuentroTerritorio.name }}</span>
            <i class="pi pi-external-link chip-ext" />
          </a>
        </div>

        <div v-if="!esPendientes && territoryMeetingPoints?.length" class="point-select-row">
          <label class="point-select-label">
            <i class="pi pi-map-marker chip-icon" />
            Puntos de encuentro en territorio
          </label>
          <div class="point-select-inline">
            <select
              class="point-select"
              :value="selectedPointIndex ?? ''"
              @change="onSelectPoint($event.target.value)"
            >
              <option value="" disabled>Elegí un punto para verlo en el mapa</option>
              <option v-for="p in territoryMeetingPoints" :key="p.index" :value="p.index">
                {{ p.index }}. {{ p.name || p.direccion }}<template v-if="p.terrKey"> — {{ p.terrKey }}</template>
              </option>
            </select>
            <a
              v-if="selectedPoint"
              :href="directionsUrl(selectedPoint)"
              target="_blank"
              class="go-btn"
            >
              <i class="pi pi-send" />
              <span>Cómo llegar</span>
            </a>
          </div>
        </div>

        <div v-if="!esPendientes && salida.gruposInvitadosData?.length" class="invitados-block">
          <div class="invitados-title">
            <i class="pi pi-users" />
            Grupos de invitados
          </div>
          <div v-for="g in salida.gruposInvitadosData" :key="g.id" class="invitado-card">
            <div v-if="g.encargado" class="encargado-row">
              <i class="pi pi-user-edit meta-icon" />
              <span class="encargado-name">{{ g.encargado }}</span>
            </div>
            <span class="invitado-detalle">
              <i class="pi pi-user meta-icon" />
              {{ g.cantidad }} persona{{ g.cantidad !== 1 ? 's' : '' }}
            </span>
            <span v-if="g.autos" class="invitado-detalle">
              <i class="pi pi-car meta-icon" />
              {{ g.autos }} vehículo{{ g.autos !== 1 ? 's' : '' }}
            </span>
            <a v-if="g.whatsapp" :href="waLink(g.whatsapp)" target="_blank" class="location-chip chip-green">
              <i class="pi pi-whatsapp chip-icon" />
              WhatsApp
            </a>
            <div v-if="g.codigo" class="codigo-inline">
              Código: {{ g.codigo }}
            </div>
          </div>
        </div>

        <div v-if="!esPendientes && territoryKeys.length" class="informar-block">
          <div class="invitados-title">
            <i class="pi pi-check-square" />
            Informar lo predicado
          </div>
          <div class="informar-btns">
            <button
              v-for="key in territoryKeys"
              :key="key"
              type="button"
              class="informar-btn"
              @click="onInformar(key)"
            >
              <i class="pi pi-pencil" />
              <span>{{ territoryKeys.length > 1 ? `Territorio ${key}` : 'Informar trabajo realizado' }}</span>
            </button>
          </div>
        </div>

        <div v-if="!esPendientes && salida.notas" class="notas-row">
          <i class="pi pi-info-circle notas-icon" />
          <span>{{ salida.notas }}</span>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.sheet-wrapper {
  position: fixed;
  inset: 0;
  z-index: 2000;
}

.sheet-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
}

.info-sheet {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--bg-grouped);
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -4px 30px rgba(0, 0, 0, 0.2);
  padding: 8px 16px;
  padding-bottom: max(env(safe-area-inset-bottom, 16px), 16px);
  max-height: 82vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

@media (min-width: 768px) {
  .info-sheet {
    top: 0;
    left: auto;
    right: 0;
    width: 26rem;
    max-height: none;
    height: 100%;
    border-radius: 20px 0 0 20px;
  }
}

.drag-handle {
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: rgba(var(--label-rgb), 0.25);
  margin: 4px auto 14px;
}

@media (min-width: 768px) {
  .drag-handle {
    display: none;
  }
}

.sheet-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.sheet-title {
  font-size: 19px;
  font-weight: 600;
  color: var(--label-primary);
  flex: 1;
}

.sheet-close-btn {
  background: rgba(var(--label-rgb), 0.12);
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(var(--label-rgb), 0.6);
  font-size: 0.75rem;
  -webkit-tap-highlight-color: transparent;
  position: relative;
}

.sheet-close-btn::after {
  content: '';
  position: absolute;
  inset: -8px;
}

.info-top-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 16px;
}

.date-block {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 5px;
}

.date-day {
  font-size: 20px;
  font-weight: 700;
  color: var(--label-primary);
  letter-spacing: -0.3px;
}

.date-full {
  font-size: 16px;
  color: rgba(var(--label-rgb), 0.55);
}

.date-hour {
  font-size: 16px;
  font-weight: 600;
  color: var(--accent);
}

.meta-block {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  color: rgba(var(--label-rgb), 0.6);
  white-space: nowrap;
}

.meta-icon {
  font-size: 0.7rem;
}

.chips-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.location-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: opacity 0.15s;
  -webkit-tap-highlight-color: transparent;
}

.location-chip:active {
  opacity: 0.65;
}

.chip-blue {
  background: var(--accent-soft);
  color: var(--accent);
  border: 1px solid rgba(0, 122, 255, 0.18);
}

.chip-green {
  background: var(--success-soft);
  color: var(--success-deep);
  border: 1px solid rgba(52, 199, 89, 0.22);
}

.chip-icon {
  font-size: 0.72rem;
}

.chip-ext {
  font-size: 0.6rem;
  opacity: 0.65;
}

.point-select-row {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 16px;
}

.point-select-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(var(--label-rgb), 0.55);
}

.point-select-inline {
  display: flex;
  align-items: center;
  gap: 8px;
}

.point-select {
  flex: 1;
  min-width: 0;
  padding: 9px 12px;
  border-radius: 10px;
  border: 1px solid rgba(0, 122, 255, 0.18);
  background: var(--accent-soft);
  color: var(--accent);
  font-size: 14px;
  font-weight: 500;
  -webkit-appearance: none;
  appearance: none;
}

.go-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  border-radius: 10px;
  background: var(--success);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
  transition: opacity 0.12s;
  -webkit-tap-highlight-color: transparent;
}

.go-btn:active {
  opacity: 0.75;
}

.go-btn i {
  font-size: 0.72rem;
}

.informar-block {
  margin-bottom: 16px;
}

.informar-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.informar-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border: none;
  border-radius: 10px;
  background: var(--success);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.12s;
  -webkit-tap-highlight-color: transparent;
}

.informar-btn:active {
  opacity: 0.75;
}

.informar-btn i {
  font-size: 0.72rem;
}

.invitados-block {
  margin-bottom: 16px;
}

.invitados-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgba(var(--label-rgb), 0.55);
  margin-bottom: 10px;
}

.invitado-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 0;
  font-size: 14px;
}

.invitado-card {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 14px;
  padding: 12px;
  border-radius: var(--radius-md);
  background: var(--bg-secondary);
  margin-bottom: 8px;
}

.invitado-card:last-child {
  margin-bottom: 0;
}

.encargado-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-basis: 100%;
}

.encargado-row .meta-icon {
  font-size: 0.9rem;
  color: var(--accent);
}

.encargado-name {
  font-size: 17px;
  font-weight: 700;
  color: var(--label-primary);
}

.codigo-inline {
  flex-basis: 100%;
  font-size: 13px;
  color: var(--label-secondary);
}

.invitado-codigo {
  font-weight: 600;
  color: var(--label-primary);
}

.invitado-detalle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  color: var(--label-primary);
}

.invitado-detalle .meta-icon {
  color: var(--label-secondary);
}

.pendientes-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

.pendientes-vacio {
  font-size: 14px;
  color: rgba(var(--label-rgb), 0.55);
}

.pendiente-card {
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--bg-primary);
}

.pendiente-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.pendiente-terr {
  font-size: 16px;
  font-weight: 600;
  color: var(--label-primary);
}

.pendiente-estado {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}

.pendiente-estado.ok {
  background: var(--success-soft);
  color: var(--success-deep);
}

.pendiente-estado.falta {
  background: var(--accent-soft);
  color: var(--accent);
}

.pendiente-mz {
  margin-top: 6px;
  font-size: 14px;
  color: rgba(var(--label-rgb), 0.6);
}

.pendiente-mz strong {
  color: var(--label-primary);
}

.pendiente-count {
  color: rgba(var(--label-rgb), 0.45);
}

.notas-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 14px;
  color: rgba(var(--label-rgb), 0.55);
  padding-top: 4px;
}

.notas-icon {
  font-size: 0.75rem;
  margin-top: 2px;
  flex-shrink: 0;
}

/* Transición con curva de iOS — las clases aplican a los hijos */
.sheet-enter-active .info-sheet,
.sheet-leave-active .info-sheet {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
}
.sheet-enter-active .sheet-backdrop,
.sheet-leave-active .sheet-backdrop {
  transition: opacity 0.3s ease;
}
.sheet-enter-from .info-sheet,
.sheet-leave-to .info-sheet {
  transform: translateY(100%);
}
@media (min-width: 768px) {
  .sheet-enter-from .info-sheet,
  .sheet-leave-to .info-sheet {
    transform: translateX(100%);
  }
}
.sheet-enter-from .sheet-backdrop,
.sheet-leave-to .sheet-backdrop {
  opacity: 0;
}
</style>
